export type AuthCookieUser = {
  userId: string
  nick: string
  avatar: string
}

type RestoredUserInput = Partial<AuthCookieUser> | null | undefined

/** 从 Cookie 字符串读取原始值，保留 URL 编码以安全写入请求头。 */
export const getRawCookieValue = (cookieString: string, name: string): string => {
  const prefix = `${name}=`
  const cookie = cookieString
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix))

  return cookie ? cookie.slice(prefix.length) : ''
}

export const decodeAuthCookieValue = (value: string) => {
  let decodedValue = value

  for (let index = 0; index < 3; index += 1) {
    try {
      const nextValue = decodeURIComponent(decodedValue)
      if (nextValue === decodedValue) {
        break
      }
      decodedValue = nextValue
    } catch {
      break
    }
  }

  return decodedValue
}

export const parseAuthCookieUser = (value: string): AuthCookieUser | null => {
  const parts = decodeAuthCookieValue(value)
    .split('|')
    .map((part) => part.trim())

  const nick = parts[1] || ''
  const avatar = parts[2] || ''
  const userId = parts[3] || ''

  if (!userId) {
    return null
  }

  return {
    userId,
    nick,
    avatar,
  }
}

// 从本地 legacy 登录 cookie 直接恢复用户，避免恢复登录态时先触发远端 401 清空本地 cookie。
export const resolveLocalAuthCookieUser = (value: string, storedUserId = ''): AuthCookieUser | null => {
  const cookieUser = parseAuthCookieUser(value)
  const normalizedStoredUserId = storedUserId.trim()

  if (!cookieUser) {
    return null
  }

  return {
    ...cookieUser,
    userId: normalizedStoredUserId || cookieUser.userId,
  }
}

// 刷新恢复登录态时，优先保留登录后已经写入的 userid cookie，再使用接口查回来的登录用户。
export const resolveRestoredAuthUser = (
  value: string,
  storedUserId = '',
  restoredUser?: RestoredUserInput,
): AuthCookieUser | null => {
  const cookieUser = parseAuthCookieUser(value)
  const normalizedStoredUserId = storedUserId.trim()
  const restoredUserId = restoredUser?.userId?.trim() || ''

  if (!cookieUser) {
    if (!restoredUserId) {
      return null
    }

    return {
      userId: normalizedStoredUserId || restoredUserId,
      nick: restoredUser?.nick || '',
      avatar: restoredUser?.avatar || '',
    }
  }

  return {
    ...cookieUser,
    nick: restoredUser?.nick || cookieUser.nick,
    avatar: restoredUser?.avatar || cookieUser.avatar,
    userId: normalizedStoredUserId || restoredUserId || cookieUser.userId,
  }
}
